import { useEffect, useState } from 'react';

import { cn } from '@/utils/cn';

import type { MultipleChoiceElementProps } from '../MultipleChoice.types';

interface CheckboxProps {
  isChecked: boolean;
  value: string;
  name: string;
  onChange: (value: string) => void;
  className?: string;
  id: string;
}

const Checkbox = ({ isChecked, name, value, onChange, className, id }: CheckboxProps) => {
  return (
    <div className="relative">
      <input
        type="checkbox"
        name={name}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        checked={isChecked}
        className="sr-only"
        id={id}
      />
      <div
        className={cn(
          'w-4 h-4 rounded border-2 cursor-pointer transition-colors duration-200',
          isChecked ? 'border-2' : 'bg-white border-gray-300 hover:border-gray-400',
          className,
        )}
        style={
          isChecked
            ? {
                backgroundColor: 'var(--pt-text-on-background-color)',
                borderColor: 'var(--pt-text-on-background-color)',
              }
            : {}
        }
        // biome-ignore lint/a11y/useSemanticElements: We need to use a custom div for styling
        role="checkbox"
        aria-checked={isChecked}
        tabIndex={0}
        onClick={() => onChange(value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onChange(value);
          }
        }}
      >
        {isChecked && (
          <svg className="w-3 h-3 absolute top-0.5 left-0.5" fill="var(--pt-background-color)" viewBox="0 0 20 20">
            <title>Checked</title>
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
    </div>
  );
};

export function MultipleChoiceCheckbox(props: MultipleChoiceElementProps) {
  const { element, onChange } = props;
  const { name, value, options, description, required, custom_field_id } = element.attrs?.data || {};

  // Internal state for optimistic UI feedback
  const initialSelectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const [selectedValues, setSelectedValues] = useState<string[]>(initialSelectedValues);

  // Keep internal state in sync if value prop changes
  useEffect(() => {
    const newSelectedValues = Array.isArray(value) ? value : value ? [value] : [];
    setSelectedValues(newSelectedValues);
  }, [value]);

  if (!name || !custom_field_id || !options) {
    return null;
  }

  const isMultipleChoice = element.type === 'multiple-choice';

  const handleCheckboxChange = (optionValue: string, isChecked: boolean) => {
    if (isMultipleChoice) {
      let newValues: string[];
      if (isChecked) {
        newValues = [...selectedValues, optionValue];
      } else {
        newValues = selectedValues.filter((val) => val !== optionValue);
      }
      setSelectedValues(newValues);
      onChange(newValues);
      return;
    }
    if (isChecked) {
      setSelectedValues([optionValue]);
      onChange(optionValue);
      return;
    }
    setSelectedValues([]);
    onChange('');
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={custom_field_id} className="font-medium text-sm text-gray-900">
          {name}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {description && <p className="text-xs text-gray-700 font-normal">{description}</p>}
      </div>
      <div className="flex flex-col gap-4">
        {options.map((option) => {
          const isChecked = selectedValues.includes(option.value);
          return (
            <div key={option.id} className="flex flex-row gap-2 items-center">
              <Checkbox
                isChecked={isChecked}
                name={custom_field_id}
                value={option.value}
                onChange={(value) => handleCheckboxChange(value, !isChecked)}
                className="w-4 h-4"
                id={`${custom_field_id}-${option.id}`}
              />
              <label
                htmlFor={`${custom_field_id}-${option.id}`}
                className={cn(
                  'text-sm cursor-pointer',
                  isChecked ? 'text-gray-900 font-medium' : 'text-gray-700 font-normal',
                )}
              >
                {option.value}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
